import { ActionContext } from 'vuex';

import * as constants from '@/store/modules/listsDashboards/lists-dashboards-types';
import { ListsDashboardSourceEnum } from '@/bundles/App/enums';
import {
  IListsDashboardsState,
  IPipelineDashboardFilters,
  IPipelineDashboardToggleFilterValuePayload,
  PipelineMeasureEnum,
  PipelineKanbanSortEnum,
} from '@/bundles/Pipeline/types';
import { pipelineServiceNest } from '@/bundles/Pipeline/factory/pipelineServiceNestFactory';
import { dealServiceNest } from '@/bundles/Deals/factory/dealServiceNestFactory';
import {
  IListsDashboardsSourcePayload,
  IDealsPipelineFilterOptions,
  ListsDashboardsActionEnum,
  DashboardViewEnum,
} from '@/bundles/DashboardAnalytics/types';
import { DealsMeasureEnum } from '@/bundles/Deals/types';
import {
  AvailabilitiesDistributionSortEnum,
  IAvailabilitiesDashboardToggleFilterValuePayload,
} from '@/bundles/Availabilities/types';
import {
  IComparablesDashboardFilters,
  IComparablesDashboardToggleFilterValuePayload,
  IComparablesSetSidePayload,
  IComparablesSetComparePayload,
  IComparablesSetCombinePayload,
} from '@/bundles/Comparables/types';

interface ICommitContext {
  commit: (type: string, payload?: unknown) => void;
}

const FILTER_OPTIONS_LOADERS: Record<ListsDashboardSourceEnum, () => Promise<IDealsPipelineFilterOptions>> = {
  [ListsDashboardSourceEnum.pipeline]: () => pipelineServiceNest.getFilterOptions(),
  [ListsDashboardSourceEnum.deals]: () => dealServiceNest.getFilterOptions(),
};

export default {
  [ListsDashboardsActionEnum.setFilters] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<Partial<IPipelineDashboardFilters> | Partial<IComparablesDashboardFilters>>,
  ) {
    commit(constants.SET_FILTERS, { source, payload });
  },
  [ListsDashboardsActionEnum.toggleFilterValue] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<IPipelineDashboardToggleFilterValuePayload | IAvailabilitiesDashboardToggleFilterValuePayload | IComparablesDashboardToggleFilterValuePayload>,
  ) {
    commit(constants.TOGGLE_FILTER_VALUE, { source, payload });
  },
  [ListsDashboardsActionEnum.resetFilters] (
    { commit }: ICommitContext,
    { source }: { source: ListsDashboardSourceEnum },
  ) {
    commit(constants.RESET_FILTERS, { source });
  },
  async [ListsDashboardsActionEnum.loadFilterOptions] (
    { commit }: ActionContext<IListsDashboardsState, any>,
    { source }: { source: ListsDashboardSourceEnum },
  ) {
    commit(constants.SET_FILTER_OPTIONS_LOADING, { source, payload: true });
    try {
      const data: IDealsPipelineFilterOptions = await FILTER_OPTIONS_LOADERS[source]();
      commit(constants.SET_FILTER_OPTIONS_DATA, { source, payload: data });
    } catch (error: any) {
      commit(constants.SET_FILTER_OPTIONS_ERROR, { source, payload: error?.message ?? 'Failed to load filter options' });
    } finally {
      commit(constants.SET_FILTER_OPTIONS_LOADING, { source, payload: false });
    }
  },
  [ListsDashboardsActionEnum.setActiveMeasure] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<PipelineMeasureEnum | DealsMeasureEnum>,
  ) {
    commit(constants.SET_ACTIVE_MEASURE, { source, payload });
  },
  [ListsDashboardsActionEnum.setKanbanSort] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<PipelineKanbanSortEnum>,
  ) {
    commit(constants.SET_KANBAN_SORT, { source, payload });
  },
  [ListsDashboardsActionEnum.setView] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<DashboardViewEnum>,
  ) {
    commit(constants.SET_VIEW, { source, payload });
  },
  [ListsDashboardsActionEnum.setDistributionSort] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<AvailabilitiesDistributionSortEnum>,
  ) {
    commit(constants.SET_DISTRIBUTION_SORT, { source, payload });
  },
  [ListsDashboardsActionEnum.setSortMetric] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<string>,
  ) {
    commit(constants.SET_SORT_METRIC, { source, payload });
  },
  [ListsDashboardsActionEnum.setSide] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<IComparablesSetSidePayload>,
  ) {
    commit(constants.SET_SIDE, { source, payload });
  },
  [ListsDashboardsActionEnum.setCompare] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<IComparablesSetComparePayload>,
  ) {
    commit(constants.SET_COMPARE, { source, payload });
  },
  [ListsDashboardsActionEnum.setCombine] (
    { commit }: ICommitContext,
    { source, payload }: IListsDashboardsSourcePayload<IComparablesSetCombinePayload>,
  ) {
    commit(constants.SET_COMBINE, { source, payload });
  },
};
